import { t } from "../../i18n";
import { createPortal } from "react-dom";
import { ChevronDown, LoaderCircle } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ApprovalHandler } from "../chat/MessageActivity";

export function WorkspaceApprovalActions({
  approvalId,
  command,
  busy,
  onApproval,
}: {
  approvalId: string;
  command: string;
  busy: boolean;
  onApproval: ApprovalHandler;
}) {
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [allowOpen, setAllowOpen] = useState(false);
  const [allowPatternMode, setAllowPatternMode] = useState(false);
  const [allowPattern, setAllowPattern] = useState("");
  const [message, setMessage] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const allowMenuRef = useRef<HTMLDivElement>(null);
  const allowPopupRef = useRef<HTMLDivElement>(null);
  const [instructionsPosition, setInstructionsPosition] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const suggestedPattern = command.trim()
    ? `${command.trim().split(/\s+/)[0]} *`
    : "";

  useEffect(() => {
    if (!instructionsOpen && !allowOpen) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !popupRef.current?.contains(target) &&
        !allowMenuRef.current?.contains(target) &&
        !allowPopupRef.current?.contains(target)
      ) {
        setInstructionsOpen(false);
        setAllowOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setInstructionsOpen(false);
        setAllowOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [instructionsOpen, allowOpen]);

  useLayoutEffect(() => {
    if (!instructionsOpen && !allowOpen) {
      setInstructionsPosition(null);
      return;
    }
    const updatePosition = () => {
      const anchor = (
        allowOpen ? allowMenuRef : menuRef
      ).current?.getBoundingClientRect();
      if (!anchor) return;
      const margin = 8;
      const width = Math.min(288, window.innerWidth - margin * 2);
      const popupHeight =
        (allowOpen ? allowPopupRef : popupRef).current?.offsetHeight ?? 220;
      const spaceBelow = window.innerHeight - anchor.bottom - margin;
      const spaceAbove = anchor.top - margin;
      const openAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;
      const availableHeight = Math.max(
        96,
        Math.min(320, openAbove ? spaceAbove : spaceBelow),
      );
      const top = openAbove
        ? Math.max(
            margin,
            anchor.top - Math.min(popupHeight, availableHeight) - margin,
          )
        : anchor.bottom + margin;
      const left = Math.max(
        margin,
        Math.min(anchor.left, window.innerWidth - width - margin),
      );
      setInstructionsPosition((current) =>
        current?.left === left &&
        current.top === top &&
        current.width === width &&
        current.maxHeight === availableHeight
          ? current
          : { left, top, width, maxHeight: availableHeight },
      );
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [instructionsOpen, allowOpen, allowPatternMode]);

  const denyWithInstructions = () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    onApproval(approvalId, "denied", trimmed);
    setInstructionsOpen(false);
  };
  const denyAndStop = () => {
    onApproval(approvalId, "denied", undefined, { stop: true });
    setInstructionsOpen(false);
  };
  const allowSimilar = () => {
    const pattern = allowPattern.trim();
    if (!pattern) return;
    onApproval(approvalId, "approved", undefined, {
      allowScope: "pattern",
      pattern,
    });
    setAllowOpen(false);
    setAllowPatternMode(false);
    setAllowPattern("");
  };

  return (
    <div className="flex flex-wrap gap-2">
      <div ref={allowMenuRef} className="relative inline-flex">
        <button
          type="button"
          className="button-primary rounded-r-none text-xs"
          disabled={busy}
          onClick={() => onApproval(approvalId, "approved")}
        >
          {busy && <LoaderCircle className="animate-spin" size={13} />}{" "}
          {t("Allow once")}
        </button>
        <button
          type="button"
          aria-label={t("Allow for this conversation")}
          aria-expanded={allowOpen}
          aria-haspopup="dialog"
          className="button-primary rounded-l-none border-l border-white/30 px-2 text-xs"
          disabled={busy}
          onClick={() => {
            setInstructionsOpen(false);
            setAllowOpen((open) => !open);
            setAllowPatternMode(false);
            setAllowPattern("");
          }}
        >
          <ChevronDown size={13} />
        </button>
        {allowOpen &&
          instructionsPosition &&
          createPortal(
            <div
              ref={allowPopupRef}
              role="dialog"
              aria-label={t("Allow command in this conversation")}
              style={{
                left: instructionsPosition.left,
                top: instructionsPosition.top,
                width: instructionsPosition.width,
                maxHeight: instructionsPosition.maxHeight,
              }}
              className="fixed z-[100] overflow-y-auto rounded-xl border border-stone-200 bg-white p-3 text-left shadow-xl dark:border-white/10 dark:bg-stone-900"
            >
              {!allowPatternMode ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="button w-full justify-start text-left text-xs"
                    disabled={busy}
                    onClick={() =>
                      onApproval(approvalId, "approved", undefined, {
                        allowScope: "exact",
                      })
                    }
                  >
                    {t("Allow this exact command in this conversation")}
                  </button>
                  <button
                    type="button"
                    className="button w-full justify-start text-left text-xs"
                    disabled={busy}
                    onClick={() => {
                      setAllowPattern(suggestedPattern);
                      setAllowPatternMode(true);
                    }}
                  >
                    {t("Allow similar commands in this conversation")}
                  </button>
                </div>
              ) : (
                <label className="text-xs font-semibold text-stone-600 dark:text-stone-300">
                  {t("Command pattern")}
                  <textarea
                    autoFocus
                    rows={3}
                    maxLength={4000}
                    value={allowPattern}
                    onChange={(event) => setAllowPattern(event.target.value)}
                    placeholder={t("e.g. git status *")}
                    className="field mt-2 resize-y font-mono text-xs"
                  />
                  <span className="mt-1 block text-[11px] font-normal text-stone-400">
                    {t("Use * for any sequence and ? for one character.")}
                  </span>
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      className="button px-2.5 py-1.5 text-xs"
                      onClick={() => setAllowPatternMode(false)}
                    >
                      {t("Back")}
                    </button>
                    <button
                      type="button"
                      className="button-primary px-2.5 py-1.5 text-xs"
                      disabled={busy || !allowPattern.trim()}
                      onClick={allowSimilar}
                    >
                      {t("Allow pattern")}
                    </button>
                  </div>
                </label>
              )}
            </div>,
            document.body,
          )}
      </div>
      <div ref={menuRef} className="relative inline-flex">
        <button
          type="button"
          className="button rounded-r-none bg-white text-xs dark:bg-white/10"
          disabled={busy}
          onClick={() => onApproval(approvalId, "denied")}
        >
          {t("Deny once")}
        </button>
        <button
          type="button"
          aria-label={t("Deny with instructions")}
          aria-expanded={instructionsOpen}
          aria-haspopup="dialog"
          className="button rounded-l-none border-l border-stone-200 bg-white px-2 text-xs dark:border-white/10 dark:bg-white/10"
          disabled={busy}
          onClick={() => {
            setAllowOpen(false);
            setInstructionsOpen((open) => !open);
          }}
        >
          <ChevronDown size={13} />
        </button>
        {instructionsOpen &&
          instructionsPosition &&
          createPortal(
            <div
              ref={popupRef}
              role="dialog"
              aria-label={t("Tell the model what to do instead")}
              style={{
                left: instructionsPosition.left,
                top: instructionsPosition.top,
                width: instructionsPosition.width,
                maxHeight: instructionsPosition.maxHeight,
              }}
              className="fixed z-[100] overflow-y-auto rounded-xl border border-stone-200 bg-white p-3 text-left shadow-xl dark:border-white/10 dark:bg-stone-900"
            >
              <label className="text-xs font-semibold text-stone-600 dark:text-stone-300">
                {t("Tell the model what to do instead")}
                <textarea
                  autoFocus
                  rows={3}
                  maxLength={4000}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      !event.shiftKey &&
                      !event.nativeEvent.isComposing &&
                      !busy &&
                      message.trim()
                    ) {
                      event.preventDefault();
                      denyWithInstructions();
                    }
                  }}
                  placeholder={t("Explain the safer alternative…")}
                  className="field mt-2 resize-y text-xs"
                />
              </label>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  className="button px-2.5 py-1.5 text-xs"
                  disabled={busy}
                  onClick={() => setInstructionsOpen(false)}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="button bg-white px-2.5 py-1.5 text-xs dark:bg-white/10"
                  disabled={busy || !message.trim()}
                  onClick={denyWithInstructions}
                >
                  {t("Deny and send")}
                </button>
              </div>
              <div className="mt-3 border-t border-stone-200 pt-3 dark:border-white/10">
                <button
                  type="button"
                  className="button w-full justify-start px-2.5 py-1.5 text-xs text-red-700 dark:text-red-300"
                  disabled={busy}
                  onClick={denyAndStop}
                >
                  {t("Deny and stop")}
                </button>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}
